import { useEffect, useMemo, useState } from "react";
import { Page } from "../../../components/page-base";
import { DatagridResponsive } from "../../../components/datagrid-base";
import { useSearchParams } from "react-router-dom";
import { TDatagridData } from "../../../components/datagrid-base/types";
import { AccountSelectContainer, Container, DgContainer } from "./styles";
import { THistoryItem, THistoryRes } from "../models";
import { useAuthContext, useNavigationContext } from "@/contexts";
import { HistoryService } from "../services";
import { Input } from "@/components/inputs";
import permissionsStore from "../../../store/permissions";
import { TAccount, TAccountRes } from "@/modules/accounts/models";
import { AccountsService } from "@/modules/accounts/services";
import { toast } from "react-toastify";
import { TSelectAccounts } from "@/modules/scenarios/models";
import { useDebounce } from "@/utils/useDebounce";
import { Tooltip } from "@mui/material";
import pdficon from "../../../assets/icons/pdficon.png";
import { useIsMobile } from "@/hooks/useIsMobile";
import HistoryIcon from "@mui/icons-material/History";

export const History: React.FC = () => {
  const {
    setSeeMoreTitle,
    selectedAccountCombo,
    setSelectedAccountCombo,
    rowsPerPage,
    setRowsPerPage,
    order
  } = useNavigationContext();
  const { role } = permissionsStore();
  const [searchParams, setSearchParams] = useSearchParams();
  const [historyRecords, setHistoryRecords] = useState<THistoryItem[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [count, setCount] = useState<number>(0);
  const [page, setPage] = useState<number>(1);

  const [accountsOptions, setAccountOptions] = useState<TSelectAccounts[]>([]);
  const { debounce } = useDebounce();
  const isMobile = useIsMobile();
  const { user } = useAuthContext();

  const busca = useMemo(() => {
    return searchParams.get("busca") || "";
  }, [searchParams]);

  const fetchAllAccounts = async () => {
    const allAccounts: TAccount[] = [];
    let page = 1;
    let total = 0;

    try {
      do {
        const response: TAccountRes | null = await AccountsService.getAccounts(
          page,
          "",
          100
        );
        if (response && response.data && Array.isArray(response.data)) {
          allAccounts.push(...response.data);
        }
        if (response && response.meta) {
          total = response.meta.total;
        } else {
          total = 0;
        }
        page++;
      } while (allAccounts.length < total);
      if (allAccounts.length) {
        if (selectedAccountCombo) {
          const selected = allAccounts.find(
            (acc) => acc.id === selectedAccountCombo
          );
          setSelectedAccountCombo(selected!.id);
        } else {
          const oldestAccount = allAccounts.reduce((oldest, current) => {
            return new Date(current.createdAt) < new Date(oldest.createdAt)
              ? current
              : oldest;
          });
          setSelectedAccountCombo(oldestAccount.id);
        }
        const options = allAccounts.map((acc) => ({
          value: acc.id.toString(),
          label: acc.name,
          payload: acc.credits
        }));
        setAccountOptions(options);
      }
    } catch (error) {
      console.error("Erro ao buscar contas:", error);
      toast.error("Erro ao buscar contas.");
    }
  };

  useEffect(() => {
    setRowsPerPage(10);
    navigateTable("1");
    fetchAllAccounts();
    /* eslint-disable-next-line */
  }, [selectedAccountCombo]);

  const fetchData = async () => {
    setLoading(true);
    try {
      if (selectedAccountCombo) {
        const response: THistoryRes = await HistoryService.getHistory(
          page,
          busca,
          selectedAccountCombo!,
          rowsPerPage,
          order
        );
        if (response.success) {
          setCount(response.meta!.total);
          if (page === 1) {
            setHistoryRecords(response.data!);
          } else {
            if (response.meta!.total > 0 && response.data!.length === 0) {
              navigateTable("1");
            }
            setHistoryRecords((prev) => [...prev, ...response.data!]);
          }
        } else {
          toast.error(response.message);
        }
      }
    } catch (error: unknown) {
      console.error("Erro ao buscar histórico:", error);
      setHistoryRecords([]);
      setCount(0);
    } finally {
      setSeeMoreTitle("Resumo:");
      setLoading(false);
    }
  };
  useEffect(() => {
    if (selectedAccountCombo) {
      fetchData();
    }
    /* eslint-disable-next-line */
  }, [busca, page, selectedAccountCombo, rowsPerPage, order]);

  const createRow = (records: THistoryItem[]): TDatagridData[] => {
    return records.map((record) => ({
      name: record.customerName,
      CPF: record.cpf,
      seeMore: record.details,
      "Data de consulta": record.createdAt,
      Status: record.status ? "✅ Aprovado" : "❌ Reprovado",
      Ações:
        record.fileUrl !== "" ? (
          <Tooltip title="Baixar registro" arrow placement="top">
            <img
              src={pdficon}
              onClick={() => handleDownload(record.fileUrl)}
              style={{
                width: "30px",
                cursor: "pointer"
              }}
            />
          </Tooltip>
        ) : (
          <span>Não disponível</span>
        ),
      id: record.id
    }));
  };

  const handleDownload = (fileUrl: string) => {
    if (fileUrl !== "") {
      window.open(fileUrl, "_blank");
    } else {
      toast.warning("Consulta sem registro para ser baixado.");
    }
  };

  const navigateTable = (newPage: string) => {
    setPage(Number(newPage));
    setSearchParams(
      {
        busca: busca
      },
      { replace: true }
    );
  };

  return (
    <Page.Root>
      <Page.Header
        title="Histórico de Consultas"
        iconPage={<HistoryIcon />}
        controlElement={[
          !isMobile && role === "owner" && user?.userType === "COMPANY" && (
            <AccountSelectContainer key="account-select">
              <span className="label">Conta selecionada:</span>
              <Input.Select
                onChange={(newValue) => {
                  setSelectedAccountCombo(Number(newValue));
                }}
                selectedValue={selectedAccountCombo?.toString()}
                options={accountsOptions}
              />
            </AccountSelectContainer>
          ),

          !isMobile && (
            <Page.Search
              key={"search-history"}
              placeholder={"Busca por nome ou CPF"}
              changeTextSearch={(texto) =>
                debounce(() => {
                  setSearchParams(
                    { busca: texto, pagina: "1" },
                    { replace: true }
                  );
                })
              }
            />
          )
        ].filter(Boolean)}
      />
      <Container>
        <div className="top-tools-container">
          {role !== "collaborator" && (
            <AccountSelectContainer key="account-select">
              <span className="label">Conta selecionada:</span>
              <Input.Select
                onChange={(newValue) => {
                  setSelectedAccountCombo(Number(newValue));
                }}
                selectedValue={selectedAccountCombo?.toString()}
                options={accountsOptions}
              />
            </AccountSelectContainer>
          )}
          <div className="search-container">
            <Page.Search
              key={"search-history"}
              placeholder={"Busca por nome ou CPF"}
              changeTextSearch={(texto) =>
                debounce(() => {
                  setSearchParams(
                    { busca: texto, pagina: "1" },
                    { replace: true }
                  );
                })
              }
            />
          </div>
        </div>
        <DgContainer>
          <DatagridResponsive.Combo
            headers={[
              { field: "Nome", width: "15vw" },
              { field: "CPF", width: "10vw" },
              { field: "Resumo", width: "30vw" },
              { field: "Data de consulta", width: "15vw", isSort: true },
              { field: "Status", width: "10vw" },
              { field: "Ações:", width: "10vw" }
            ]}
            count={count}
            rowHeader="name"
            data={createRow(historyRecords)}
            navigateTable={navigateTable}
            loading={loading}
            page={page}
          />
        </DgContainer>
      </Container>
    </Page.Root>
  );
};
